import { useState, useEffect } from 'react';
import { Client, Pagination } from '../types/client';

interface UseClientsReturn {
  clients: Client[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  pagination: Pagination;
  fetchClients: (isLoadMore?: boolean, searchTerm?: string) => Promise<void>;
  handleLoadMore: () => void;
  deleteClient: (clientId: string) => Promise<void>;
}

export const useClients = (): UseClientsReturn => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  const fetchClients = async (isLoadMore = false, searchTerm = '') => {
    try {
      if (!isLoadMore) {
        setLoading(true);
        setClients([]);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const currentOffset = isLoadMore ? pagination.offset + pagination.limit : 0;
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: currentOffset.toString(),
      });
      
      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      
      const response = await fetch(`/api/clients/list?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          clients: Client[];
          pagination?: Pagination;
          error?: string;
        };
        
        if (data.success) {
          if (isLoadMore) {
            setClients(prev => [...prev, ...data.clients]);
          } else {
            setClients(data.clients);
          }
          
          if (data.pagination) {
            setPagination(data.pagination);
          }
        } else {
          throw new Error(data.error || 'Failed to load clients');
        }
      } else {
        throw new Error('Failed to load clients');
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && pagination.hasMore) {
      fetchClients(true);
    }
  };

  const deleteClient = async (clientId: string) => {
    if (!confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/clients/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ clientId })
      });

      if (response.ok) {
        await fetchClients();
      } else {
        throw new Error('Failed to delete client');
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      alert('Failed to delete client. Please try again.');
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return {
    clients,
    loading,
    loadingMore,
    error,
    pagination,
    fetchClients,
    handleLoadMore,
    deleteClient
  };
}; 